import { AdditionalInfoCard } from "@/app/ui/AdditionalInfoCard";
import FlightDetailsCard from "@/app/ui/FlightDetailsCard/FlightDetailsCard";
import { Alert, Button } from "@mui/material";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import BottomDrawer from "@/app/ui/flight-purchase/BottomDrawer";
import {
  BnplTrackingEvents,
  FlightTrackingEvents,
  rudderAnalytics,
} from "@/app/lib/rudderstack";
import { formatCurrency } from "@/app/lib/utils/currency";
import PaymentChooser from "@/app/ui/PaymentChooser";
import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import { useCreatePendingInstallment } from "@/app/lib/hooks/useCreatePendingInstallment";
import { Feature, isFeatureEnabled } from "@/app/lib/utils/featureFlags";
import {
  includeBnplFee,
  calculateMonthlyInstallment,
} from "@/app/lib/utils/bnpl-fee";
import { Drawer, DrawerTopBar } from "@/app/ui/Drawer";
import { BaggageAllowance } from "@/app/ui/BaggageAllowance";
import { useFlightSelectionPayloadContext } from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useQuery } from "@tanstack/react-query";
import { flightAvailabilityQueryOptions } from "@/app/lib/hooks/useFlightAvailability";
import { useSelectedDepartureFlight } from "@/app/lib/hooks/flightSearch/useSelectedDepartureFlight";
import { useSelectedReturnFlight } from "@/app/lib/hooks/flightSearch/useSelectedReturnFlight";

export const Route = createFileRoute(
  "/_authenticated/_flights/_flight-offer/flights/offer/$id/step/1",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const { selectedFlightPayload } = useFlightSelectionPayloadContext();
  const { result: selectedDepartureFlight } = useSelectedDepartureFlight();
  const { result: selectedReturnFlight } = useSelectedReturnFlight();
  const navigate = Route.useNavigate();
  const t = useTranslations();

  const { data: flightAvailabilityData, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));

  const {
    paymentType,
    selectedInstallments,
    maxAvailableInstallments,
    isEligibilityLoading,
    refreshSession,
    isEligible,
  } = usePaymentContext();

  const [isContinuing, setIsContinuing] = useState(false);
  const [showPaymentChooser, setShowPaymentChooser] = useState(false);
  const isBnplFeatureEnabled =
    isFeatureEnabled(Feature.Bnpl) && Boolean(isEligible);

  const {
    mutateAsync: createPendingInstallment,
    isError: isCreatePendingInstallmentError,
  } = useCreatePendingInstallment();

  const originalPrice = flightAvailabilityData?.totalPrice || 0;

  // Calculate BNPL fee if BNPL is selected
  const includedBnplFee = includeBnplFee(originalPrice);
  const totalPrice = paymentType === "bnpl" ? includedBnplFee : originalPrice;

  const handleContinue = () => {
    if (isBnplFeatureEnabled) {
      setShowPaymentChooser(true);
      return;
    }
    if (isContinuing) return;
    setIsContinuing(true);

    rudderAnalytics.track(
      FlightTrackingEvents.USER_CONTINUED_AFTER_FLIGHT_DETAILS,
      {
        bookingType: "Flight",
        selected_offer_id: flightAvailabilityData?.index,
        price: totalPrice,
      },
    );
    navigate({ to: "/flights/offer/$id/step/2" });
    setIsContinuing(false);
  };

  const handlePaymentChooserContinue = async () => {
    if (isContinuing) return;
    setIsContinuing(true);

    rudderAnalytics.track(
      FlightTrackingEvents.USER_CONTINUED_AFTER_FLIGHT_DETAILS,
      {
        bookingType: "Flight",
        selected_offer_id: flightAvailabilityData?.index,
        price: totalPrice,
      },
    );

    const eventName =
      paymentType === "bnpl"
        ? BnplTrackingEvents.USER_SELECTED_BNPL_PAYMENT
        : BnplTrackingEvents.USER_SELECTED_STANDARD_PAYMENT;

    rudderAnalytics.track(eventName, {
      is_bnpl_eligible: isEligible,
    });

    try {
      if (paymentType === "bnpl") {
        const { data: eligibilityData } = await refreshSession();
        if (eligibilityData?.isEligible) {
          await createPendingInstallment({
            sessionId: eligibilityData.sessionId,
            amount: originalPrice,
            countOfMonth: selectedInstallments,
          });
        }
      }
      navigate({ to: "/flights/offer/$id/step/2" });
    } finally {
      setIsContinuing(false);
      setShowPaymentChooser(false);
    }
  };

  return (
    <>
      <div className="px-4">
        <div className="mt-2 mb-4">
          {selectedDepartureFlight && (
            <div className="flex flex-col gap-2">
              <div className="section-title mb-0">
                {selectedReturnFlight
                  ? t("flights.departureFlight")
                  : t("flights.flightDetails")}
              </div>
              <FlightDetailsCard
                flight={selectedDepartureFlight}
                offerItems={selectedFlightPayload?.offerItems}
                isDepartureFlight
              />
              <BaggageAllowance
                baggageData={flightAvailabilityData?.outboundBaggage}
                isLoading={isAdditionalDataLoading}
              />
            </div>
          )}

          {selectedReturnFlight && (
            <div className="mt-4">
              <div className="flex flex-col gap-2">
                <div className="section-title mb-0">
                  {t("flights.returnFlight")}
                </div>
                <FlightDetailsCard
                  flight={selectedReturnFlight}
                  offerItems={selectedFlightPayload?.offerItems}
                />
                {flightAvailabilityData?.returnBaggage !== null ? (
                  <BaggageAllowance
                    baggageData={flightAvailabilityData?.returnBaggage}
                    isLoading={isAdditionalDataLoading}
                  />
                ) : null}
              </div>
            </div>
          )}
        </div>

        <AdditionalInfoCard />
      </div>

      <BottomDrawer
        totalPrice={formatCurrency(originalPrice)}
        showPayLater
        payLaterPrice={formatCurrency(
          calculateMonthlyInstallment(
            includedBnplFee,
            maxAvailableInstallments,
          ),
        )}
        buttonLabel={t("flights.continue")}
        onButtonClick={handleContinue}
        disabled={
          isAdditionalDataLoading || isContinuing || isEligibilityLoading
        }
      />
      {isCreatePendingInstallmentError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t("payment.pendingInstallmentError")}
        </Alert>
      )}

      <Drawer
        open={showPaymentChooser}
        onClose={() => setShowPaymentChooser(false)}
      >
        <DrawerTopBar
          onCloseClick={() => setShowPaymentChooser(false)}
          title={t("payment.paymentTypeHeader")}
        />

        <PaymentChooser price={originalPrice} />

        <Button
          fullWidth
          variant="contained"
          onClick={handlePaymentChooserContinue}
          disabled={isContinuing}
        >
          {t("flights.continue")}
        </Button>
      </Drawer>
    </>
  );
}
